import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ADS_ANALYTIC_EVENTS, GOOGLE_AD_CONVERSION_ID, GOOGLE_GA4_MEASUREMENT_ID } from '@/lib/constants';

declare global {
    interface Window {
        gtag?: (
            command: 'event' | 'config' | 'js' | 'set',
            targetId: string | Date,
            config?: Record<string, any>
        ) => void;
        dataLayer?: any[];
    }
}

interface GoogleAnalyticsEventParams {
    [key: string]: any;
}

interface GoogleAnalyticsContextValue {
    trackEvent: (eventName: string, params?: GoogleAnalyticsEventParams) => void;
    trackConversion: (conversionAction: string, params?: GoogleAnalyticsEventParams) => void;
    trackAdsEvent: (eventType: keyof typeof ADS_ANALYTIC_EVENTS, params?: GoogleAnalyticsEventParams) => void;
    setUserProperties: (properties: Record<string, any>) => void;
}

const GoogleAnalyticsContext = createContext<GoogleAnalyticsContextValue | null>(null);

/**
 * Provider that handles Google Analytics page view tracking and provides event tracking functions
 */
export function GoogleAnalyticsProvider({ children }: { children: React.ReactNode }) {
    const location = useLocation();

    // Track page views on route change
    useEffect(() => {
        if (window.gtag) {
            window.gtag('config', GOOGLE_GA4_MEASUREMENT_ID, {
                page_path: location.pathname + location.search,
            });
        }
    }, [location]);

    const trackEvent = useCallback((eventName: string, params?: GoogleAnalyticsEventParams) => {
        if (window.gtag) {
            window.gtag('event', eventName, {
                ...params,
                send_to: [GOOGLE_GA4_MEASUREMENT_ID, GOOGLE_AD_CONVERSION_ID],
            });
        }
    }, []);

    const trackConversion = useCallback((conversionAction: string, params?: GoogleAnalyticsEventParams) => {
        if (window.gtag) {
            window.gtag('event', 'conversion', {
                send_to: conversionAction,
                ...params,
            });
        }
    }, []);

    const trackAdsEvent = useCallback(
        (eventType: keyof typeof ADS_ANALYTIC_EVENTS, params?: GoogleAnalyticsEventParams) => {
            const eventName = ADS_ANALYTIC_EVENTS[eventType];
            if (eventName && window.gtag) {
                window.gtag('event', eventName, {
                    ...params,
                    send_to: [GOOGLE_GA4_MEASUREMENT_ID, GOOGLE_AD_CONVERSION_ID],
                });
            }
        },
        []
    );

    const setUserProperties = useCallback((properties: Record<string, any>) => {
        if (window.gtag) {
            window.gtag('set', 'user_properties', properties);
        }
    }, []);

    const value = useMemo<GoogleAnalyticsContextValue>(
        () => ({
            trackEvent,
            trackConversion,
            trackAdsEvent,
            setUserProperties,
        }),
        [trackEvent, trackConversion, trackAdsEvent, setUserProperties]
    );

    return <GoogleAnalyticsContext.Provider value={value}>{children}</GoogleAnalyticsContext.Provider>;
}

/**
 * Hook to access Google Analytics tracking functions
 * Must be used within GoogleAnalyticsProvider
 */
export function useGoogleAnalytics() {
    const context = useContext(GoogleAnalyticsContext);
    if (!context) {
        throw new Error('useGoogleAnalytics must be used within GoogleAnalyticsProvider');
    }
    return context;
}
